// src/components/Inventory/CataloguePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CatalogueForm from "./CatalogueForm";
import { FaBoxOpen, FaSearch, FaEdit, FaTrash, FaImage, FaPlus } from "react-icons/fa";

const CataloguePage = () => {
  const { t } = useTranslation();

  // Données & UI
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [sortMode, setSortMode] = useState("name_asc");
  const [perPage, setPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);

  // Edition
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Helper d'URL image (gère les chemins absolus/relatifs à l'API)
  const API_BASE = (api?.defaults?.baseURL || "").replace(/\/$/, "");
  const resolveImgUrl = (u) => {
    if (!u) return null;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith("/")) return `${API_BASE}${u}`;
    return `${API_BASE}/${u}`;
  };

  // Chargement
  const fetchItems = () => {
    api
      .get("/inventory")
      .then((res) => setItems(res.data))
      .catch(() => toast.error(t("catalogue.loadError")));
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtrage + tri
  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = items;

    if (q) {
      out = out.filter((it) => (it.name || "").toLowerCase().includes(q));
    }

    const byNameAsc = (a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    const byNameDesc = (a, b) => -byNameAsc(a, b);
    const byVolAsc = (a, b) => Number(a.volume ?? 0) - Number(b.volume ?? 0);
    const byVolDesc = (a, b) => -byVolAsc(a, b);

    switch (sortMode) {
      case "name_desc":
        out = [...out].sort(byNameDesc);
        break;
      case "vol_asc":
        out = [...out].sort(byVolAsc);
        break;
      case "vol_desc":
        out = [...out].sort(byVolDesc);
        break;
      case "name_asc":
      default:
        out = [...out].sort(byNameAsc);
        break;
    }
    return out;
  }, [items, query, sortMode]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / perPage));
  const page = Math.min(currentPage, totalPages);
  const start = (page - 1) * perPage;
  const paginatedItems = filteredSorted.slice(start, start + perPage);

  const goFirst = () => setCurrentPage(1);
  const goPrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const goNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));
  const goLast = () => setCurrentPage(totalPages);
  const goTo = (p) => setCurrentPage(p);

  // Fenêtre de pagination (style standard : quelques pages autour)
  const visibleWindow = 5; // nombre max de boutons numériques visibles
  const half = Math.floor(visibleWindow / 2);
  let startPage = Math.max(1, page - half);
  let endPage = Math.min(totalPages, startPage + visibleWindow - 1);
  if (endPage - startPage + 1 < visibleWindow) {
    startPage = Math.max(1, endPage - visibleWindow + 1);
  }
  const pages = [];
  for (let p = startPage; p <= endPage; p++) pages.push(p);

  const handleDelete = (id) => setConfirmDeleteId(id);

  const confirmDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await api.delete(`/inventory/${id}`);
      toast.success(t("quote.deleteSuccess", "Article supprimé"));
      const after = items.length - 1;
      const newTotalPages = Math.max(1, Math.ceil(after / perPage));
      if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
      fetchItems();
    } catch {
      toast.error(t("quote.deleteFail", "Échec de la suppression."));
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-500/15 to-orange-500/5 flex items-center justify-center text-orange-500 shrink-0 ring-1 ring-orange-500/10">
          <FaBoxOpen className="text-lg" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-base-content">{t("quote.manageCatalog", "Manage Inventory Catalogue")}</h1>
          <p className="text-sm text-base-content/40 mt-0.5">{t("quote.manageCatalogSubtitle", "Browse, add, and manage your inventory items")}</p>
        </div>
      </div>

      {/* Barre d'outils */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mt-6 mb-6 bg-base-100 border border-base-200 rounded-2xl shadow-sm p-3">
        <div className="relative w-full md:w-72">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/30 text-xs" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCurrentPage(1);
            }}
            placeholder={t("quote.searchPlaceholder", "Search items…")}
            className="input input-bordered input-sm bg-base-200 border-base-300 focus:border-primary focus:outline-none text-base-content w-full pl-8"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-base-content/40 hidden sm:inline">
            {filteredSorted.length} {t("quote.itemsCount", "articles")}
          </span>

          <select
            value={sortMode}
            onChange={(e) => {
              setSortMode(e.target.value);
              setCurrentPage(1);
            }}
            className="select select-bordered select-sm bg-base-200 border-base-300 text-base-content"
            aria-label={t("quote.sortBy", "Sort by")}
          >
            <option value="name_asc">A→Z</option>
            <option value="name_desc">Z→A</option>
            <option value="vol_asc">m³ ↑</option>
            <option value="vol_desc">m³ ↓</option>
          </select>

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="select select-bordered select-sm bg-base-200 border-base-300 text-base-content"
            aria-label={t("quote.itemsPerPage", "Items per page")}
          >
            <option value={8}>8 / page</option>
            <option value={12}>12 / page</option>
            <option value={24}>24 / page</option>
            <option value={48}>48 / page</option>
          </select>

          <button
            onClick={() => {
              setEditingItem(null);
              setShowForm(true);
            }}
            className="btn btn-primary btn-sm gap-1.5"
          >
            <FaPlus className="text-xs" /> {t("quote.addItem", "Add Item")}
          </button>
        </div>
      </div>

      {/* Grille */}
      {paginatedItems.length === 0 ? (
        <div className="text-center text-base-content/40 py-16 border-2 border-dashed border-base-300 rounded-2xl bg-base-100/50">
          <FaBoxOpen className="mx-auto text-3xl text-base-content/15 mb-3" />
          {query
            ? t("quote.noResults", "No items match your search.")
            : t("quote.noItemsYet", "No items yet. Add your first one!")}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {paginatedItems.map((item) => {
            const src = resolveImgUrl(item.image_url);
            return (
              <div
                key={item.id}
                className="group relative bg-base-100 rounded-2xl border border-base-200 shadow-sm hover:shadow-lg hover:border-primary/30 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
              >
                {/* Actions overlay */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    className="w-7 h-7 rounded-lg bg-base-100/90 backdrop-blur text-amber-500 hover:bg-amber-500 hover:text-white flex items-center justify-center shadow-sm transition-colors"
                    onClick={() => {
                      setEditingItem(item);
                      setShowForm(true);
                    }}
                    aria-label={t("quote.editItem", "Edit Item")}
                  >
                    <FaEdit className="text-xs" />
                  </button>
                  <button
                    className="w-7 h-7 rounded-lg bg-base-100/90 backdrop-blur text-error hover:bg-error hover:text-white flex items-center justify-center shadow-sm transition-colors"
                    onClick={() => handleDelete(item.id)}
                    aria-label={t("quote.deleteItem", "Delete Item")}
                  >
                    <FaTrash className="text-xs" />
                  </button>
                </div>

                <div className="aspect-square bg-base-200/60 flex items-center justify-center overflow-hidden">
                  {src ? (
                    <img
                      src={src}
                      alt={item.name}
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-200"
                      loading="lazy"
                    />
                  ) : (
                    <FaImage className="text-2xl text-base-content/15" />
                  )}
                </div>

                <div className="p-3">
                  <h3 className="font-semibold text-sm text-base-content truncate" title={item.name}>
                    {item.name}
                  </h3>
                  <p className="text-xs text-base-content/40 mt-0.5">
                    {Number(item.volume ?? 0)} m³
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-1.5 mt-8">
          <button
            onClick={goFirst}
            disabled={page === 1}
            className="px-2.5 py-1.5 text-sm border border-base-300 rounded-lg disabled:opacity-30 hover:bg-base-200 text-base-content transition-colors"
            aria-label={t("quote.firstPage", "First page")}
          >
            «
          </button>
          <button
            onClick={goPrev}
            disabled={page === 1}
            className="px-2.5 py-1.5 text-sm border border-base-300 rounded-lg disabled:opacity-30 hover:bg-base-200 text-base-content transition-colors"
            aria-label={t("quote.previousPage", "Previous page")}
          >
            ‹
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => goTo(1)}
                className="px-2.5 py-1.5 text-sm border border-base-300 rounded-lg hover:bg-base-200 text-base-content transition-colors"
              >
                1
              </button>
              {startPage > 2 && <span className="px-1 text-base-content/30 text-sm">…</span>}
            </>
          )}

          {pages.map((p) => (
            <button
              key={p}
              onClick={() => goTo(p)}
              className={`px-2.5 py-1.5 text-sm border rounded-lg transition-colors ${
                p === page
                  ? "bg-primary text-primary-content border-primary font-semibold"
                  : "border-base-300 hover:bg-base-200 text-base-content"
              }`}
            >
              {p}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="px-1 text-base-content/30 text-sm">…</span>
              )}
              <button
                onClick={() => goTo(totalPages)}
                className="px-2.5 py-1.5 text-sm border border-base-300 rounded-lg hover:bg-base-200 text-base-content transition-colors"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={goNext}
            disabled={page === totalPages}
            className="px-2.5 py-1.5 text-sm border border-base-300 rounded-lg disabled:opacity-30 hover:bg-base-200 text-base-content transition-colors"
            aria-label={t("quote.nextPage", "Next page")}
          >
            ›
          </button>
          <button
            onClick={goLast}
            disabled={page === totalPages}
            className="px-2.5 py-1.5 text-sm border border-base-300 rounded-lg disabled:opacity-30 hover:bg-base-200 text-base-content transition-colors"
            aria-label={t("quote.lastPage", "Last page")}
          >
            »
          </button>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-xl border border-base-200 shadow-2xl p-6 max-w-sm w-full animate-fade-in">
            <h3 className="font-bold text-base-content text-base mb-1">{t("quote.confirmDelete")}</h3>
            <p className="text-sm text-base-content/50 mb-5">{t("quote.deleteItem")}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDeleteId(null)} className="btn btn-ghost btn-sm">{t("buttons.cancel")}</button>
              <button onClick={confirmDelete} className="btn btn-error btn-sm">{t("buttons.delete")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire (modal) */}
      {showForm && (
        <CatalogueForm
          item={editingItem}
          onClose={() => {
            setShowForm(false);
            setEditingItem(null);
            fetchItems();
          }}
        />
      )}
    </div>
  );
};

export default CataloguePage;
